import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiClient } from "../../config/api";
import API_CONFIG from "../../config/api";

// Transform API response to match component expected format
const transformCustomerRequest = (apiRequest) => {
  // Format date string to match component format
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date
        .toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
        .replace(/(\d+)\/(\d+)\/(\d+),?/g, "$3-$1-$2")
        .replace(/\//g, "-")
        .replace(", ", " ");
    } catch {
      return dateString;
    }
  };

  // Map severity to priority
  const mapSeverityToPriority = (severity) => {
    if (!severity) return "Medium";
    const normalized = (severity || "").toLowerCase();
    switch (normalized) {
      case "critical":
        return "Critical";
      case "high":
        return "High";
      case "medium":
        return "Medium";
      case "low":
        return "Low";
      default:
        return severity || "Medium";
    }
  };

  // Derive category from tags or description
  const deriveCategory = (tags, description) => {
    if (tags) {
      const tagStr = tags.toLowerCase();
      if (
        tagStr.includes("compliance") ||
        tagStr.includes("soc") ||
        tagStr.includes("audit")
      ) {
        return "Compliance";
      }
      if (tagStr.includes("incident") || tagStr.includes("breach")) {
        return "Incident Response";
      }
      if (tagStr.includes("documentation") || tagStr.includes("report")) {
        return "Documentation";
      }
    }
    if (description) {
      const descStr = description.toLowerCase();
      if (descStr.includes("questionnaire") || descStr.includes("compliance")) {
        return "Compliance";
      }
      if (descStr.includes("report")) {
        return "Documentation";
      }
      if (descStr.includes("incident")) {
        return "Incident Response";
      }
    }
    return "General";
  };

  // Derive request type
  const deriveRequestType = (tags, description) => {
    if (tags) {
      const tagStr = tags.toLowerCase();
      if (tagStr.includes("security") || tagStr.includes("assessment")) {
        return "Security Assessment";
      }
      if (tagStr.includes("report")) {
        return "Report Delivery";
      }
      if (tagStr.includes("inquiry") || tagStr.includes("question")) {
        return "Security Inquiry";
      }
      if (tagStr.includes("audit")) {
        return "Audit Report";
      }
    }
    if (description) {
      const descStr = description.toLowerCase();
      if (descStr.includes("questionnaire")) {
        return "Security Assessment";
      }
      if (descStr.includes("report")) {
        return "Report Delivery";
      }
      if (descStr.includes("inquiry")) {
        return "Security Inquiry";
      }
    }
    return "General Request";
  };

  return {
    id:
      apiRequest.id?.toString() ||
      `REQ-${apiRequest.clientID || "UNK"}-${apiRequest.id || "000"}`,
    title: apiRequest.description
      ? apiRequest.description.length > 60
        ? apiRequest.description.substring(0, 60) + "..."
        : apiRequest.description
      : "Customer Request",
    description: apiRequest.description || "",
    customer: apiRequest.companyName || "Unknown Company",
    contact: apiRequest.assignPerson || apiRequest.createdBy || "N/A",
    email: apiRequest.email || "",
    phone: apiRequest.phoneNumber || "",
    priority: mapSeverityToPriority(apiRequest.severity),
    status: apiRequest.status || "Open",
    category: deriveCategory(apiRequest.tags, apiRequest.description),
    requestType: deriveRequestType(apiRequest.tags, apiRequest.description),
    created:
      formatDate(apiRequest.createdOn) ||
      formatDate(apiRequest.modifiedOn) ||
      new Date().toISOString(),
    dueDate: formatDate(apiRequest.resolvedOn) || null,
    assignee: apiRequest.assignPerson || "Unassigned",
    lastResponse:
      formatDate(apiRequest.resolvedOn) ||
      formatDate(apiRequest.modifiedOn) ||
      null,
    // Keep original API fields for reference
    workspaceId: apiRequest.workspaceId,
    createdBy: apiRequest.createdBy,
    resolvedBy: apiRequest.resolvedBy,
    tags: apiRequest.tags,
    attachmentIds: apiRequest.attachmentIds,
    clientID: apiRequest.clientID,
    clientUUID: apiRequest.clientUUID,
  };
};

export const fetchCustomerRequests = createAsyncThunk(
  "customerRequests/fetchCustomerRequests",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        API_CONFIG.ENDPOINTS.CUSTOMER_REQUESTS,
        {
          headers: {
            Accept: "text/plain",
          },
        }
      );

      // Handle array response
      const data = Array.isArray(response.data) ? response.data : [];
      return data.map(transformCustomerRequest);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch customer requests";
      return rejectWithValue(message);
    }
  }
);

export const createCustomerRequest = createAsyncThunk(
  "customerRequests/createCustomerRequest",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        API_CONFIG.ENDPOINTS.CUSTOMER_REQUESTS,
        requestData
      );
      return transformCustomerRequest(response.data);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to create customer request";
      return rejectWithValue(message);
    }
  }
);

export const updateCustomerRequest = createAsyncThunk(
  "customerRequests/updateCustomerRequest",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(
        `${API_CONFIG.ENDPOINTS.CUSTOMER_REQUESTS}/${id}`,
        data
      );
      return transformCustomerRequest(response.data);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to update customer request";
      return rejectWithValue(message);
    }
  }
);

export const deleteCustomerRequest = createAsyncThunk(
  "customerRequests/deleteCustomerRequest",
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`${API_CONFIG.ENDPOINTS.CUSTOMER_REQUESTS}/${id}`);
      return id;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete customer request";
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  requests: [],
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

const customerRequestsSlice = createSlice({
  name: "customerRequests",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    resetState(state) {
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchCustomerRequests.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCustomerRequests.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.requests = action.payload || [];
        state.error = null;
      })
      .addCase(fetchCustomerRequests.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch customer requests";
      })
      // Create
      .addCase(createCustomerRequest.pending, (state) => {
        state.error = null;
      })
      .addCase(createCustomerRequest.fulfilled, (state, action) => {
        state.requests.unshift(action.payload);
        state.error = null;
      })
      .addCase(createCustomerRequest.rejected, (state, action) => {
        state.error = action.payload || "Failed to create customer request";
      })
      // Update
      .addCase(updateCustomerRequest.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCustomerRequest.fulfilled, (state, action) => {
        const index = state.requests.findIndex(
          (r) => r.id === action.payload.id
        );
        if (index !== -1) {
          state.requests[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateCustomerRequest.rejected, (state, action) => {
        state.error = action.payload || "Failed to update customer request";
      })
      // Delete
      .addCase(deleteCustomerRequest.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteCustomerRequest.fulfilled, (state, action) => {
        state.requests = state.requests.filter((r) => {
          const requestId = r.id?.toString();
          const deletedId = action.payload?.toString();
          return !requestId?.endsWith(deletedId) && requestId !== deletedId;
        });
        state.error = null;
      })
      .addCase(deleteCustomerRequest.rejected, (state, action) => {
        state.error = action.payload || "Failed to delete customer request";
      });
  },
});

export const { clearError, resetState } = customerRequestsSlice.actions;
export default customerRequestsSlice.reducer;
